/**
 * 技术特征高亮服务
 * 负责管理技术特征在文本中的高亮显示、重叠检测和交互
 */

/**
 * 技术特征高亮服务 DI 标识
 */
export const TechnicalFeatureHighlightService = Symbol.for('TechnicalFeatureHighlightService');

/**
 * 技术特征匹配项
 */
export interface TechnicalFeatureMatch {
    /** 技术特征名称 */
    techFeature: string;
    /** 对比特征文本,多个特征用换行符分隔 */
    comparisonFeature: string;
}

/**
 * 高亮元素配置
 */
export interface HighlightElementConfig {
    /** DOM 元素 */
    element: HTMLElement;
    /** 特征文本 */
    text: string;
    /** 是否添加字母标识 */
    addLetter: boolean;
    /** 字母标识 */
    letter: string;
    /** 字母颜色 */
    color: string;
    /** 背景颜色 */
    bgColor: string;
    /** 特征索引 */
    featureIndex: number;
}

/**
 * hover 事件处理器
 */
export interface HoverEventHandlers {
    /** 鼠标进入高亮元素 */
    onMouseEnter: (event: Event) => void;
    /** 鼠标离开高亮元素 */
    onMouseLeave: (event: Event) => void;
}

/**
 * 待处理的模糊匹配项
 */
export interface PendingFuzzyMatch {
    /** 技术特征名称 */
    techFeature: string;
    /** 待匹配的文本 */
    text: string;
    /** 字母标识 */
    letter: string;
    /** 字母颜色 */
    color: string;
    /** 背景颜色 */
    bgColor: string;
    /** 特征索引 */
    featureIndex: number;
}

/**
 * 两阶段高亮回调函数
 */
export interface TwoPhaseHighlightCallbacks {
    /** 第一阶段完成后的回调 */
    onFirstPhaseComplete?: () => void;
    /** 第二阶段开始前的回调 */
    onSecondPhaseStart?: (pendingCount: number) => void;
    /** 第二阶段完成后的回调 */
    onSecondPhaseComplete?: () => void;
    /** 发生错误时的回调 */
    onError?: (error: Error) => void;
}

/**
 * 两阶段高亮配置参数
 */
export interface TwoPhaseHighlightConfig {
    /** 容器元素 */
    container: HTMLElement;
    /** 技术特征匹配项列表 */
    matches: TechnicalFeatureMatch[];
    /** 获取样式的函数 */
    getStyles: (techFeature: string) => { letter: string; color: string; bgColor: string };
    /** 原文文本（用于模糊匹配） */
    originText: string;
    /** 文本语言 */
    language?: 'CN' | 'EN' | 'JP' | 'TW';
    /** 事件处理器(用于添加监听) */
    handlers?: HoverEventHandlers;
    /** 两阶段高亮回调函数 */
    callbacks?: TwoPhaseHighlightCallbacks;
}

/**
 * 技术特征高亮服务接口
 */
export interface TechnicalFeatureHighlightService {
    /**
     * 调整技术特征文本
     * 1. 去掉开头和中间的段落号[xxxx]格式
     * 2. 去掉省略号'... '（包括后面可能的空格）
     * 3. 去掉末尾的分号、逗号、句号（支持中英文标点）
     * 4. 去掉首尾空白字符
     * @param text 原始文本
     * @returns 调整后的文本
     */
    adjustText(text: string): string;

    /**
     * 高亮单个元素
     * @param config 高亮配置
     */
    highlightElement(config: HighlightElementConfig): void;

    /**
     * 清除指定容器中的所有高亮标记
     * @param container 容器元素
     * @param handlers 事件处理器(用于移除监听)
     */
    clearHighlight(container: HTMLElement, handlers?: HoverEventHandlers): void;

    /**
     * 为高亮元素添加 hover 事件监听
     * @param container 容器元素
     * @param handlers 事件处理器
     */
    attachHoverListeners(container: HTMLElement, handlers: HoverEventHandlers): void;

    /**
     * 移除高亮元素的 hover 事件监听
     * @param container 容器元素
     * @param handlers 事件处理器
     */
    detachHoverListeners(container: HTMLElement, handlers: HoverEventHandlers): void;

    /**
     * 两阶段高亮技术特征
     * 第一阶段：快速高亮所有能精确匹配的文本
     * 第二阶段：异步调用模糊匹配API，高亮无法精确匹配的文本
     * @param config 两阶段高亮配置参��
     */
    startTwoPhaseHighlight(config: TwoPhaseHighlightConfig): Promise<void>;
}
